import { Application, Assets, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import type { ThemeAssetEntry } from '../../theme';
import { loadUiFonts } from '../board/ui';
import { AgeGateScreen, type AgeGateResult, type AgeGateUi } from './AgeGateScreen';

/** Where the gate's art lives: the first slot's own, so a first visit
 *  fetches nothing the slot won't use anyway. */
export interface AgeGateArtUrls {
  background: string;
  column: string;
  border: string;
  coin: string;
  /** Reel symbols, the rarer ones last. */
  symbols: readonly string[];
}

export interface MountAgeGateOptions {
  state: 'ask' | 'under';
  art: AgeGateArtUrls;
  /** The painted button plates the answers are drawn on. */
  plates?: Record<string, ThemeAssetEntry>;
  onResult: (result: AgeGateResult) => void;
  /** Called once the gate is drawn (the host can drop its placeholder). */
  onReady?: () => void;
}

declare global {
  interface Window {
    /** The age check on screen, as plain data, for browser tests. */
    __ageGate?: {
      ui: () => AgeGateUi;
      press: (label: string) => boolean;
      where: (label: string) => { x: number; y: number } | null;
    };
  }
}

async function load(url: string): Promise<Texture | null> {
  try {
    return await Assets.load<Texture>(url);
  } catch {
    return null;
  }
}

/**
 * Mount the age check into `el` on its own transparent Pixi canvas, over
 * whatever the app has underneath. Returns a teardown.
 */
export function mountAgeGate(el: HTMLElement, options: MountAgeGateOptions): () => void {
  const app = new Application();
  let gone = false;
  let screen: AgeGateScreen | null = null;
  // Listened for in the capture phase and kept from the game underneath:
  // a space or an "i" typed at the gate mustn't spin the reels or open the
  // paytable behind it.
  const onKey = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    event.stopPropagation();
    if (screen?.key(event.key)) event.preventDefault();
  };
  // Destroying before init has finished throws (React StrictMode mounts
  // twice), so teardown waits for it.
  const ready = (async () => {
    await app.init({
      resizeTo: el,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio,
      autoDensity: true,
    });
    if (gone) return;
    el.appendChild(app.canvas);
    const { art } = options;
    const [background, column, border, coin, symbols] = await Promise.all([
      load(art.background),
      load(art.column),
      load(art.border),
      load(art.coin),
      Promise.all(art.symbols.map(load)),
      loadUiFonts(),
      options.plates ? AssetLoader.shared.loadAssets(options.plates).catch(() => undefined) : undefined,
    ]);
    if (gone) return;
    screen = new AgeGateScreen({
      art: {
        background,
        column,
        border,
        coin,
        floor: column,
        symbols: symbols.filter((t): t is Texture => t !== null),
      },
      ticker: app.ticker,
      state: options.state,
      onResult: options.onResult,
    });
    app.stage.addChild(screen);
    const fit = (): void => screen?.layout(app.screen.width, app.screen.height);
    fit();
    app.renderer.on('resize', fit);
    window.addEventListener('keydown', onKey, true);
    window.__ageGate = {
      ui: () => screen!.ui(),
      press: (label) => screen!.press(label),
      where: (label) => screen!.where(label),
    };
    options.onReady?.();
  })();
  return () => {
    gone = true;
    window.removeEventListener('keydown', onKey, true);
    delete window.__ageGate;
    void ready.finally(() => {
      // The reels run on the app's ticker, and the app's own destroy takes
      // the ticker down before its stage: the gate goes first, while its
      // reels can still stop.
      screen?.destroy({ children: true });
      screen = null;
      // The textures are the slot's too: leave them loaded. And keep Pixi's
      // shared resources: `destroy(true)` would release them, and the slot's
      // own renderer, still running, would then throw on every frame and
      // stay blank. Only this canvas goes.
      app.destroy({ removeView: true }, { children: true });
    });
  };
}
